import React, { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import zrsLogo from "@/brand/zrsLogo";
import { supabase, supabaseConfigured } from "@/lib/supabaseClient";
import { useI18n } from "@/i18n/I18nProvider";

const DiscordGlyph = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M20.317 4.369a19.8 19.8 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.056a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.105a13.1 13.1 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127a12.3 12.3 0 0 1-1.873.893a.077.077 0 0 0-.041.105c.36.699.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.84 19.84 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028Zm-12.5 10.955c-1.183 0-2.157-1.086-2.157-2.42c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.095 2.157 2.42c0 1.333-.956 2.419-2.157 2.419Zm7.975 0c-1.183 0-2.157-1.086-2.157-2.42c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.095 2.157 2.42c0 1.333-.946 2.419-2.157 2.419Z" />
    </svg>
);

export default function LoginModal({ open, onClose }) {
    const [loading, setLoading] = useState(false);
    /** { key } (mensagem traduzível) ou { text } (mensagem do Supabase, tal como vem). */
    const [error, setError] = useState(null);
    const { t } = useI18n();

    const handleDiscord = useCallback(async () => {
        setError(null);
        if (!supabaseConfigured || !supabase) {
            setError({ key: "login.errorNotConfigured" });
            return;
        }
        setLoading(true);
        const redirectTo = `${window.location.origin}${window.location.pathname}`;
        const { error: oauthError } = await supabase.auth.signInWithOAuth({
            provider: "discord",
            options: { redirectTo },
        });
        setLoading(false);
        if (oauthError) {
            setError(oauthError.message ? { text: oauthError.message } : { key: "login.errorStart" });
        }
    }, []);

    useEffect(() => {
        if (!open) {
            setError(null);
            setLoading(false);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            data-testid="login-modal"
            className="fixed inset-0 z-[120] flex items-center justify-center px-4 py-8"
        >
            <button
                aria-label={t("common.close")}
                data-testid="login-modal-backdrop"
                onClick={onClose}
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            />

            <div
                className="relative w-full max-w-md rounded-2xl border border-white/10 overflow-hidden"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(26,15,10,0.98), rgba(10,7,6,0.98))",
                    boxShadow: "0 40px 120px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,184,0,0.12)",
                }}
            >
                <button
                    onClick={onClose}
                    data-testid="login-modal-close"
                    aria-label={t("common.close")}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-zrs-offwhite/70 hover:text-gold hover:bg-white/5"
                >
                    <X size={16} />
                </button>

                <div className="pt-8 px-7 pb-2 text-center">
                    <img
                        src={zrsLogo}
                        alt="ZRS"
                        className="w-12 h-12 mx-auto mb-3 object-contain"
                        style={{ filter: "drop-shadow(0 0 10px rgba(255,184,0,0.5))" }}
                    />
                    <div className="text-[10px] uppercase tracking-[0.4em] text-zrs-metal">
                        {t("login.eyebrow")}
                    </div>
                </div>

                <div className="px-7 pt-6 pb-8 space-y-4">
                    <div className="text-center">
                        <h2 className="text-lg font-bold text-zrs-offwhite">{t("login.title")}</h2>
                        <p className="text-[13px] text-zrs-metal mt-2 leading-relaxed">
                            {t("login.desc")}
                        </p>
                    </div>

                    <button
                        type="button"
                        data-testid="login-discord-btn"
                        disabled={loading}
                        onClick={handleDiscord}
                        className="w-full inline-flex items-center justify-center gap-3 py-3.5 rounded-xl font-bold text-[13px] tracking-wide border border-white/10 text-zrs-offwhite hover:border-white/30 hover:bg-white/5 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                    >
                        <DiscordGlyph className="w-5 h-5 shrink-0" />
                        {loading ? t("login.redirecting") : t("login.continueDiscord")}
                    </button>

                    {error && (
                        <p
                            data-testid="login-discord-error"
                            className="text-center text-[13px] text-red-400/90 leading-snug"
                        >
                            {error.key ? t(error.key) : error.text}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
